import { useState, useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import { RightArrow } from 'components/common';
import Text from 'components/ui/Text';
import Button from 'components/entrance/Button';
import { Switch } from 'components/form';

import useKakaoChannel from 'hooks/useKakaoChannel';
import { updateMarketingAgreeSettings } from 'redux/slices/user/userSlice';
import { applyHighlight } from 'utils/applyHighlight';
import { formatKeepOpenChange } from 'components/all/_lib/formatKeepOpenChange';

import { RotateCcw } from 'lucide-react';

import { ReactComponent as SuccessIcon } from 'assets/icons/check-icon.svg';
import { ReactComponent as FailureIcon } from 'assets/icons/danger-icon.svg';

const DOOR_OPEN_TIMEOUT_SESAME = 5000;
const DOOR_OPEN_TIMEOUT_KIOSK = 10000;

const MenuSections = ({
  sections,
  state,
  info,
  page,
  onDoorOpen,
  onToggleAlwaysOpen,
  onRefresh,
  setDoorMessage,
  kiosks = [],
  keepOpenLoading = {},
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.user);
  const marketingPushAgree = !!userInfo.marketingDtm;

  const [disabledDoorOpenButtons, setDisabledDoorOpenButtons] = useState({});
  // 상태 확인 불가 기기의 재조회(새로고침) 진행 여부 (키오스크별)
  const [refreshingKiosks, setRefreshingKiosks] = useState({});

  const doorStatusResetTimerRefs = useRef({});

  // 상태 확인 불가 기기의 상태 재조회. 회전 애니메이션 동안 중복 클릭 막음
  const handleRefreshStatus = async (kioskName) => {
    if (!onRefresh || refreshingKiosks[kioskName]) return;

    setRefreshingKiosks((prev) => ({ ...prev, [kioskName]: true }));
    try {
      await onRefresh();
    } finally {
      setRefreshingKiosks((prev) => ({ ...prev, [kioskName]: false }));
    }
  };

  const { handleClickOpenKakao } = useKakaoChannel();

  const handleNavigate = (to, state = {}) => {
    navigate(to, { state });
  };

  const handleMarketingToggle = (e) => {
    const checked = e.target.checked;
    const marketingYN = checked ? 'Y' : 'N';
    dispatch(updateMarketingAgreeSettings({ value: marketingYN }));
  };

  // TODO: Custom Hook으로 분리
  // statusType이 success일 때 버튼 비활성화 및 타이머 설정
  useEffect(() => {
    kiosks.forEach((kiosk) => {
      const kioskName = kiosk.kioskName;

      if (kiosk.statusType === 'success') {
        // 기존 타이머가 있으면 클리어
        if (doorStatusResetTimerRefs.current[kioskName]) {
          clearTimeout(doorStatusResetTimerRefs.current[kioskName]);
        }

        // 상시 문열림 OFF일 경우: 출입문 열기 버튼 활성화
        if (kiosk.isKeepOpen === false) {
          setDisabledDoorOpenButtons((prev) => ({
            ...prev,
            [kioskName]: false,
          }));
        } else {
          // 상시 문열기가 ON일 경우: 출입문 열기 버튼 비활성화
          setDisabledDoorOpenButtons((prev) => ({
            ...prev,
            [kioskName]: true,
          }));
        }

        // 참깨 기기는 5초, 키오스크(isKeepOpen === null)는 10초 동안 버튼 비활성화 상태 유지
        const doorStatusResetTimeout = kiosk.isKeepOpen === null ? DOOR_OPEN_TIMEOUT_KIOSK : DOOR_OPEN_TIMEOUT_SESAME;

        // 타이머 설정
        doorStatusResetTimerRefs.current[kioskName] = setTimeout(() => {
          if (kiosk.isKeepOpen === false || kiosk.isKeepOpen === null) {
            // 활성화(false) 로직
            setDisabledDoorOpenButtons((prev) => ({
              ...prev,
              [kioskName]: false,
            }));
          } else {
            setDisabledDoorOpenButtons((prev) => ({
              ...prev,
              [kioskName]: true,
            }));
          }

          delete doorStatusResetTimerRefs.current[kioskName];

          setDoorMessage((prev) => ({
            ...prev,
            [kioskName]: {
              ...prev[kioskName],
              statusMessage: null,
              statusType: null,
            },
          }));
        }, doorStatusResetTimeout);
      }
    });

    return () => {
      const doorStatusResetTimers = doorStatusResetTimerRefs.current;
      Object.values(doorStatusResetTimers).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kiosks]);

  if (page === 'terms') {
    return (
      <SectionsList>
        {sections.map((section, index) => (
          <Section
            key={index}
            onClick={() =>
              handleNavigate(section.to, {
                label: section.label.replace(' (보기)', ''),
                name: section.name,
              })
            }>
            <p>{applyHighlight('(보기)', section.label)}</p>
            <RightArrow />
          </Section>
        ))}
        <Switch
          value='marketingPushAgree'
          label='마케팅 활용 및 광고성 정보 수신'
          checked={marketingPushAgree}
          onChange={handleMarketingToggle}
        />
      </SectionsList>
    );
  }

  if (page === 'setting') {
    return (
      <SectionsList>
        {sections.map((section, index) => {
          // 링크 이동 메뉴
          if (section.to) {
            return (
              <Section key={index} onClick={() => handleNavigate(section.to, { ...state, ...info })}>
                <p>{section.label}</p>
                <RightArrow />
              </Section>
            );
          }

          // 출입문 제어 메뉴
          if (section.action === 'openDoor') {
            return (
              <DoorControlSection key={index}>
                <Section>
                  <p>{section.label}</p>
                </Section>

                <DoorControlContainer>
                  {kiosks.map((kiosk, kioskIndex) => {
                    const kioskName = kiosk.kioskName;
                    // 참깨(스마트락) 연동 기기 여부 판별 (일반 키오스크는 상시 문열기 없음)
                    const isSesame = kiosk.sesameSynced === true;
                    // 참깨 기기지만 벤더 지연으로 상시개방 상태 확인 불가 (리스트/열기는 정상)
                    const isStatusUnknown = isSesame && kiosk.isKeepOpen == null;
                    // 참깨 기기: 관리자앱에서 마지막으로 상시 문열기를 바꾼 사람·시각 (이력 없으면 null)
                    const keepOpenChangeText = isSesame ? formatKeepOpenChange(kiosk.lastKeepOpenChange) : null;

                    return (
                      <KioskControlItem key={kioskName}>
                        {kioskIndex > 0 && <Divider />}
                        <div>
                          <DoorControlRow>
                            <SpaceName>{kiosk.spaceName}</SpaceName>

                            {/* 참깨 기기: 상태 확인 불가면 배지 + 재조회, 정상이면 상시 문열기 토글 */}
                            {isSesame &&
                              (isStatusUnknown ? (
                                <UnknownStatusGroup>
                                  <UnknownStatusBadge>상시 문열기 상태 확인 불가</UnknownStatusBadge>
                                  <RefreshButton
                                    type='button'
                                    aria-label='상태 다시 확인'
                                    disabled={!!refreshingKiosks[kioskName]}
                                    onClick={() => handleRefreshStatus(kioskName)}>
                                    <RefreshIcon
                                      as={RotateCcw}
                                      size={16}
                                      $spinning={!!refreshingKiosks[kioskName]}
                                    />
                                  </RefreshButton>
                                </UnknownStatusGroup>
                              ) : (
                                <AlwaysOpenToggle>
                                  <AlwaysOpenText>상시 문열기</AlwaysOpenText>
                                  <ToggleSwitchWrapper>
                                    <ToggleSwitchInput
                                      type='checkbox'
                                      id={`always-open-${kioskIndex}`}
                                      checked={kiosk.isKeepOpen}
                                      disabled={!!keepOpenLoading[kiosk.kioskName]}
                                      onChange={() => {
                                        onToggleAlwaysOpen(kiosk);
                                      }}
                                    />
                                    <ToggleSwitchLabel htmlFor={`always-open-${kioskIndex}`} />
                                  </ToggleSwitchWrapper>
                                </AlwaysOpenToggle>
                              ))}
                          </DoorControlRow>

                          {keepOpenChangeText && <KeepOpenChangeText>{keepOpenChangeText}</KeepOpenChangeText>}

                          <DoorControlActions>
                            <HistoryButton
                              type='button'
                              onClick={() =>
                                handleNavigate('/all/settings/center/door-history', {
                                  ...state,
                                  kioskName,
                                  spaceName: kiosk.spaceName,
                                })
                              }>
                              기록 보기
                            </HistoryButton>
                            <DoorButton
                              onClick={() => onDoorOpen(kiosk.kioskName)}
                              disabled={isStatusUnknown || kiosk.isKeepOpen || disabledDoorOpenButtons[kioskName]}>
                              출입문 열기
                            </DoorButton>
                          </DoorControlActions>

                          {isStatusUnknown && (
                            <StatusMessage type='error'>
                              <FailureIcon width={12} height={12} fill='#DA2E2E' />
                              <StatusText>참깨 연결이 원활하지 않아요. 바디코디 고객센터로 문의해 주세요.</StatusText>
                            </StatusMessage>
                          )}

                          {kiosk.statusMessage && (
                            <StatusMessage type={kiosk.statusType || 'success'}>
                              {kiosk.statusType === 'error' ? (
                                <FailureIcon width={12} height={12} fill='#DA2E2E' />
                              ) : kiosk.statusType === 'loading' ? null : (
                                <SuccessIcon width={12} height={12} fill='#004FEC' />
                              )}
                              <StatusText>{kiosk.statusMessage}</StatusText>
                            </StatusMessage>
                          )}
                        </div>
                      </KioskControlItem>
                    );
                  })}
                </DoorControlContainer>
              </DoorControlSection>
            );
          }

          return null;
        })}
      </SectionsList>
    );
  }

  return (
    <SectionsList>
      <SectionsBox>
        {sections.slice(0, 2).map((section, index) => (
          <Section key={index} onClick={() => handleNavigate(section.to)}>
            <p>{section.label}</p>
            <RightArrow />
          </Section>
        ))}
      </SectionsBox>
      <SectionsBox>
        {sections.slice(2, 4).map((section, index) => {
          if (section.label === '앱 버전') {
            return (
              <Section key={index}>
                <p>{section.label}</p>
                <VersionText>1.0.0</VersionText>
              </Section>
            );
          }
          return (
            <Section key={index} onClick={() => handleNavigate(section.to)}>
              <p>{section.label}</p>
              <RightArrow />
            </Section>
          );
        })}
      </SectionsBox>
      <SectionsBox>
        <SectionCs>
          <Text lineHeight='24px'>고객센터</Text>
          <Text
            color='gray40'
            lineHeight='24px'
            css={css`
              margin: 8px 0 16px;
            `}>
            서비스 관련 문의는 바디코디 카카오 고객센터 챗봇을 이용해주세요
          </Text>
          <Button
            color='primary50'
            border='primary50'
            background='white'
            onClick={handleClickOpenKakao}
            css={css`
              border-radius: 8px;
            `}>
            서비스 문의하기
          </Button>
        </SectionCs>
      </SectionsBox>
    </SectionsList>
  );
};

const SectionsList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;

  & > div > h4 {
    font-weight: 500;
  }
`;

const SectionsBox = styled.div`
  & + & {
    margin-top: 16px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 56px;

  p {
    font-size: 16px;
    line-height: 24px;
    font-weight: 500;
    color: ${(props) => props.theme.primaryTextColor};
  }
`;

const SectionCs = styled.div`
  padding: 16px 0;
`;

const DoorControlContainer = styled.div`
  border: 1px solid #e1e6ed;
  border-radius: 8px;
  padding: 16px;
  overflow: hidden;
`;

const Divider = styled.div`
  height: 1px;
  background: #f0f3f9;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const DoorControlRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const KeepOpenChangeText = styled.p`
  margin: 0 0 10px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: ${(props) => props.theme.colors['gray50']};
  word-break: keep-all;
`;

const SpaceName = styled.p`
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  color: #202020;
`;

const AlwaysOpenToggle = styled.div`
  display: flex;
  flex-direction: row;
  align-content: center;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 24px;
`;

const AlwaysOpenText = styled.span`
  font-size: 14px;
  line-height: 16px;
  font-weight: 500;
  color: #26282b;
`;

const UnknownStatusGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const UnknownStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  background: #fdecec;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  color: #da2e2e;
`;

const ToggleSwitchWrapper = styled.div`
  position: relative;
  width: 30.6px;
  height: 18px;
`;

const ToggleSwitchInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 30.6px;
  height: 18px;
  margin: 0;
  padding: 0;
  opacity: 0;
  z-index: 2;
  cursor: pointer;

  &:checked + label {
    background: #004fec;
    &::after {
      left: calc(100% - 16px);
    }
  }
`;

const ToggleSwitchLabel = styled.label`
  position: absolute;
  top: 0;
  left: 0;
  width: 30.6px;
  height: 18px;
  border-radius: 9px;
  background: #d6d9de;
  cursor: pointer;
  transition: background 0.2s;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.2s;
  }
`;

const DoorControlActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 4px;
`;

const DoorButton = styled.button`
  background: #fff;
  border: 1px solid #d3d5d9;
  border-radius: 6px;
  padding: 6px 12px;
  width: 91px;
  height: 36px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  letter-spacing: -0.2px;
  color: #1f6aff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #f5f9ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: #999;
  }
`;

// 출입문 열기 옆 보조 버튼 — 같은 크기, 회색 글씨로 주 동작(열기)과 구분
const HistoryButton = styled.button`
  background: #fff;
  border: 1px solid #d3d5d9;
  border-radius: 6px;
  padding: 6px 12px;
  width: 91px;
  height: 36px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  letter-spacing: -0.2px;
  color: #3c3f44;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f5f6f8;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(-360deg); }
`;

const RefreshButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #5b6675;
  transition: background 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    background: #e9ebef;
    color: #26282b;
  }

  &:active:not(:disabled) {
    background: #dde0e5;
  }

  &:disabled {
    cursor: default;
  }
`;

const RefreshIcon = styled.svg`
  ${({ $spinning }) =>
    $spinning &&
    css`
      animation: ${spin} 0.6s linear infinite;
    `}
`;

const STATUS_COLORS = {
  error: { text: '#DA2E2E', bg: '#FDECEC' },
  loading: { text: '#5B6675', bg: '#F2F4F7' },
  success: { text: '#004FEC', bg: '#EDF3FF' },
};

const StatusMessage = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  margin-top: 8px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: ${(props) => (STATUS_COLORS[props.type] || STATUS_COLORS.success).text};
  background: ${(props) => (STATUS_COLORS[props.type] || STATUS_COLORS.success).bg};

  svg {
    flex-shrink: 0;
    /* 첫 줄(line-height 18px) 세로 중앙에 아이콘(12px)을 맞춤 */
    margin-top: 3px;
  }
`;

const StatusText = styled.span`
  flex: 1;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  text-align: left;
  word-break: keep-all;
  overflow-wrap: break-word;
`;

const VersionText = styled.p`
  color: ${(props) => props.theme.colors['gray50']};
`;

const DoorControlSection = styled.div`
  width: 100%;
`;

const KioskControlItem = styled.div`
  width: 100%;
`;

export default MenuSections;
